import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

import "../styles/theme-dark.css"; // tema oscuro global

const API_BASE =
  window.location.hostname === "localhost"
    ? "http://localhost:5000/api"
    : "https://panel.corex3.com/api";

// Helper API
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_BASE}${path}`, opts);
  if (!res.ok) throw new Error(`HTTP ${res.status} en ${path}`);
  return res.json();
};


// Plantilla empresa vacía
const emptyCompany = {
  id: null,
  nombre: "",
  nif: "",
  direccion: "",
  telefono: "",
  email: "",
  web: "",
  almacen_direccion: "",
  iban: "",
  banco: "",
  titular: "",
  prefijo_presupuesto: "",
  inicio_numeracion_presupuesto: "",
  prefijo_factura: "",
  inicio_numeracion_factura: "",
  logoUrl: "",
  logoWidthMm: 35,
};

export default function CompanySettings() {
  const [companies, setCompanies] = useState([]);
  const [current, setCurrent] = useState({ ...emptyCompany });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    load();
  }, []);

  // Cargar datos
  async function load() {
    try {
      setError("");
      const data = await api("/companies")
;

      const list = Array.isArray(data) ? data : data ? [data] : [];
      setCompanies(list);

      if (list.length) {
        const c = list[0];
        setCurrent({ ...emptyCompany, ...c });
        setLogoPreview(c.logoUrl ? `${API_BASE}${c.logoUrl}` : "");

      } else {
        setCurrent({ ...emptyCompany });
        setLogoPreview("");
      }
    } catch (err) {
      setError("No se han podido cargar los datos de la empresa.");
      setCurrent({ ...emptyCompany });
    }
  }

  // Inputs
  function onChange(e) {
    const { name, value } = e.target;
    setCurrent((prev) => ({ ...prev, [name]: value }));
  }

  // Guardar
  async function save() {
    if (!current) return;
    setSaving(true);
    setError("");

    try {
      await api(`/companies/${current.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(current),
      });
      await load();
    } catch (err) {
      console.error(err);
      setError("No se han podido guardar los datos de la empresa.");
    } finally {
      setSaving(false);
    }
  }

  async function saveAndExit() {
    await save();
    navigate("/ajustes");
  }

  function exit() {
    navigate("/ajustes");
  }

  // Logo
async function uploadLogo(file) {
  if (!current) return;
  const fd = new FormData();
  fd.append("logo", file);
  fd.append("logoWidthMm", current.logoWidthMm || 35);

  const res = await fetch(
    `${API_BASE}/companies/${current.id}/logo`,
    { method: "POST", body: fd }
  ).then((r) => r.json());

  setCurrent((prev) => ({
    ...prev,
    logoUrl: res.logoUrl,
    logoWidthMm: res.logoWidthMm,
  }));

  setLogoPreview(`${API_BASE}${res.logoUrl}`);
}


  // ======================================================================
  //                      RENDER — FULL DARK GLASS
  // ======================================================================
  return (
    <div className="page-dark" style={{ maxWidth: 1000, margin: "0 auto" }}>
      
      {/* =======================================================
            HEADER GLASS OSCURO (igual que PaymentPresets)
      ======================================================== */}
      <div className="glass-box" style={{ marginBottom: 26 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          
          {/* IZQUIERDA */}
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
              Datos de la Empresa
            </h1>
            <p style={{ opacity: 0.85, marginTop: 4 }}>
              Información fiscal, contacto, logo, numeración y ajustes base.
            </p>
          </div>

          {/* BOTÓN VOLVER */}
          <button
            onClick={() => navigate("/ajustes")}
            className="glass-btn"
            style={{ padding: "10px 20px", borderRadius: 12 }}
          >
            ← Volver
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{ color: "#ff8b8b", marginBottom: 15, fontSize: 14 }}
        >
          {error}
        </div>
      )}

      {/* =======================================================
            TARJETA PRINCIPAL
      ======================================================== */}
      <div className="glass-box">

        {/* =============================
              DATOS BÁSICOS
        ============================== */}
        <h2 style={{ fontSize: 20, marginBottom: 10 }}>Datos Básicos</h2>
        <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <label>Nombre</label>
            <input className="glass-input" name="nombre" value={current.nombre} onChange={onChange} />
          </div>

          <div>
            <label>NIF</label>
            <input className="glass-input" name="nif" value={current.nif} onChange={onChange} />
          </div>

          <div>
            <label>Dirección</label>
            <input className="glass-input" name="direccion" value={current.direccion} onChange={onChange} />
          </div>

          <div>
            <label>Teléfono</label>
            <input className="glass-input" name="telefono" value={current.telefono} onChange={onChange} />
          </div>

          <div>
            <label>Email</label>
            <input className="glass-input" name="email" value={current.email} onChange={onChange} />
          </div>

          <div>
            <label>Web</label>
            <input className="glass-input" name="web" value={current.web} onChange={onChange} />
          </div>

          <div style={{ gridColumn: "1 / 3" }}>
            <label>Dirección almacén (origen envío)</label>
            <input
              className="glass-input"
              name="almacen_direccion"
              value={current.almacen_direccion}
              onChange={onChange}
            />
          </div>
        </div>

        <hr style={{ margin: "26px 0", opacity: 0.2 }} />

        {/* =============================
              DATOS BANCARIOS
        ============================== */}
        <h2 style={{ fontSize: 20, marginBottom: 10 }}>Datos Bancarios</h2>
        <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <label>IBAN (para PDFs)</label>
            <input
              className="glass-input"
              name="iban"
              value={current.iban}
              onChange={onChange}
              placeholder="ES00 0000 0000 0000 0000 0000"
            />
          </div>

          <div>
            <label>Banco</label>
            <input
              className="glass-input"
              name="banco"
              value={current.banco}
              onChange={onChange}
              placeholder="Nombre del banco"
            />
          </div>

          <div>
            <label>Titular</label>
            <input
              className="glass-input"
              name="titular"
              value={current.titular}
              onChange={onChange}
            />
          </div>
        </div>

        <hr style={{ margin: "26px 0", opacity: 0.2 }} />

        {/* =============================
              NUMERACIÓN
        ============================== */}
        <h2 style={{ fontSize: 20, marginBottom: 10 }}>Numeración de Documentos</h2>
        <div className="form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          
          <div>
            <label>Prefijo Presupuestos</label>
            <input
              className="glass-input"
              name="prefijo_presupuesto"
              value={current.prefijo_presupuesto}
              onChange={onChange}
              placeholder="P-2025-"
            />
          </div>

          <div>
            <label>Número inicial Presupuestos</label>
            <input
              type="number"
              className="glass-input"
              name="inicio_numeracion_presupuesto"
              value={current.inicio_numeracion_presupuesto}
              onChange={onChange}
            />
          </div>

          <div>
            <label>Prefijo Facturas</label>
            <input
              className="glass-input"
              name="prefijo_factura"
              value={current.prefijo_factura}
              onChange={onChange}
              placeholder="F-2025-"
            />
          </div>

          <div>
            <label>Número inicial Facturas</label>
            <input
              type="number"
              className="glass-input"
              name="inicio_numeracion_factura"
              value={current.inicio_numeracion_factura}
              onChange={onChange}
            />
          </div>
        </div>

        <hr style={{ margin: "26px 0", opacity: 0.2 }} />

        {/* =============================
              LOGO
        ============================== */}
        <h2 style={{ fontSize: 20, marginBottom: 10 }}>Logo para Documentos</h2>

        <div style={{ display: "flex", gap: 26, alignItems: "center", flexWrap: "wrap" }}>

          {/* Vista del logo */}
          <div>
            <p style={{ fontWeight: 600, marginBottom: 6 }}>Logo actual</p>
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="logo"
                style={{
                  maxHeight: 80,
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.08)",
                  padding: 6,
                }}
              />
            ) : (
              <p style={{ opacity: 0.6 }}>No hay logo</p>
            )}
          </div>

          {/* Input archivo */}
          <input
            type="file"
            accept="image/*"
            className="glass-btn"
            style={{ padding: "8px 14px" }}
            onChange={(e) =>
              e.target.files?.[0] && uploadLogo(e.target.files[0])
            }
          />

          {/* Ancho PDF */}
          <div>
            <label>Ancho en PDF (mm)</label>
            <input
              type="number"
              min="10"
              max="120"
              className="glass-input"
              value={current.logoWidthMm}
              onChange={(e) =>
                setCurrent((prev) => ({
                  ...prev,
                  logoWidthMm: Number(e.target.value),
                }))
              }
              style={{ width: 90 }}
            />
          </div>
        </div>

        <hr style={{ margin: "26px 0", opacity: 0.15 }} />

        {/* Enlace a formas de pago */}
        <div style={{ marginBottom: 20 }}>
          <Link to="/ajustes/pagos" className="glass-btn" style={{ padding: "8px 16px" }}>
            Gestionar Formas de Pago
          </Link>
        </div>

        {/* =============================
              BOTONES FINALES
        ============================== */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={exit} className="glass-btn">
            Salir
          </button>

          <button onClick={save} disabled={saving} className="glass-btn">
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>

          <button
            onClick={saveAndExit}
            disabled={saving}
            className="glass-btn"
          >
            {saving ? "Guardando…" : "Guardar y salir"}
          </button>
        </div>
      </div>
    </div>
  );
}
